import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    ShowButton,
    DeleteButton,
    Filter,
    TextInput,
    SearchInput,
    SelectInput,
    DateInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    BulkDeleteButton,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Chip,
    Box,
    Typography,
    Stack,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Info,
    Campaign,
    Warning,
    Person,
    Notifications,
    Schedule,
    Check,
    Close,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const MessageFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.messages.filters.search')}
                source="q"
                alwaysOn
            />
            <SelectInput
                source="type"
                label={translate('resources.messages.fields.type')}
                choices={[
                    { id: 'announcement', name: translate('resources.messages.types.announcement') },
                    { id: 'promotion', name: translate('resources.messages.types.promotion') },
                    { id: 'system', name: translate('resources.messages.types.system') },
                    { id: 'personal', name: translate('resources.messages.types.personal') },
                    { id: 'alert', name: translate('resources.messages.types.alert') },
                ]}
            />
            <SelectInput
                source="priority"
                label={translate('resources.messages.fields.priority')}
                choices={[
                    { id: 'low', name: translate('resources.messages.priorities.low') },
                    { id: 'normal', name: translate('resources.messages.priorities.normal') },
                    { id: 'high', name: translate('resources.messages.priorities.high') },
                    { id: 'urgent', name: translate('resources.messages.priorities.urgent') },
                ]}
            />
            <DateInput
                source="created_after"
                label={translate('resources.messages.filters.createdAfter')}
            />
            <DateInput
                source="created_before"
                label={translate('resources.messages.filters.createdBefore')}
            />
        </Filter>
    );
};

const MobileMessageFilter = (props: any) => {
    return (
        <Filter {...props}>
            <SearchInput source="q" alwaysOn />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1, flexWrap: 'wrap', width: '100%' }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

const wrappedChipSx = {
    maxWidth: '100%',
    minWidth: 0,
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const TypeIcon = ({ type }: { type: string }) => {
    switch (type) {
        case 'announcement':
            return <Campaign fontSize="small" color="primary" />;
        case 'promotion':
            return <Info fontSize="small" color="success" />;
        case 'system':
            return <Notifications fontSize="small" color="action" />;
        case 'personal':
            return <Person fontSize="small" color="info" />;
        case 'alert':
            return <Warning fontSize="small" color="error" />;
        default:
            return <Info fontSize="small" />;
    }
};

const PriorityChip = ({ priority }: { priority: string }) => {
    const translate = useTranslate();
    let color: 'default' | 'primary' | 'warning' | 'error' = 'default';

    switch (priority) {
        case 'low':
            color = 'default';
            break;
        case 'normal':
            color = 'primary';
            break;
        case 'high':
            color = 'warning';
            break;
        case 'urgent':
            color = 'error';
            break;
    }

    return (
        <Chip
            label={translate(`resources.messages.priorities.${priority}`)}
            color={color}
            size="small"
            sx={{
                height: 'auto',
                ...wrappedChipSx,
                '& .MuiChip-label': {
                    ...wrappedChipSx['& .MuiChip-label'],
                    whiteSpace: 'nowrap',
                },
            }}
        />
    );
};

const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    let color: 'default' | 'primary' | 'success' | 'error' = 'default';
    let icon;

    switch (status) {
        case 'draft':
            color = 'default';
            break;
        case 'scheduled':
            color = 'primary';
            icon = <Schedule fontSize="small" />;
            break;
        case 'sent':
            color = 'success';
            icon = <Check fontSize="small" />;
            break;
        case 'failed':
            color = 'error';
            icon = <Close fontSize="small" />;
            break;
        default:
            status = 'sent';
            color = 'success';
            icon = <Check fontSize="small" />;
            break;
    }

    return (
        <Chip
            label={translate(`resources.messages.statuses.${status}`)}
            color={color}
            size="small"
            icon={icon}
            sx={{ height: 'auto', ...wrappedChipSx }}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            {data.map((record: any) => {
                let status = 'sent';
                const now = new Date();
                const validFrom = new Date(record.valid_from);
                if (validFrom > now) {
                    status = 'scheduled';
                } else if (record.total_recipients === 0) {
                    status = 'draft';
                }

                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/messages/${record.id}/show`)}
                            sx={{
                                py: 1.5,
                                px: 2,
                                borderBottom: '1px solid #F0F0F0',
                                cursor: 'pointer',
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                overflowX: 'hidden',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.5, width: '100%', maxWidth: '100%', minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                                    <TypeIcon type={record.type} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                        {record.title}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
                                    <StatusChip status={status} />
                                    <PriorityChip priority={record.priority} />
                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {record.total_reads}/{record.total_recipients} {translate('resources.messages.fields.reads')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {record.created_at ? new Date(record.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}
                            </Typography>
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

const MessageList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? <MobileMessageFilter /> : <MessageFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                sort={{ field: 'created_at', order: 'DESC' }}
                perPage={25}
                empty={isMobile ? false : undefined}
                sx={{
                    maxWidth: '100%',
                    '& .RaList-main, & .RaList-content, & .RaList-actions, & .RaList-noResults': {
                        maxWidth: '100%',
                        minWidth: 0,
                    },
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                        maxWidth: '100%',
                    },
                    ...(isMobile && {
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important', px: '0 !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            display: 'flex !important',
                            flexWrap: 'wrap !important',
                            flex: '1 1 100% !important',
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                            margin: '0 !important',
                        },
                        '& .RaFilterForm-spacer': { display: 'none !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput, & .RaFilterForm-input': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                            marginRight: '0 !important',
                        },
                        '& .filter-field .MuiFormControl-root, & .RaFilterForm-input .MuiFormControl-root': {
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                        },
                        '& .filter-field .MuiInputBase-root, & .RaFilterForm-input .MuiInputBase-root': {
                            width: '100% !important',
                            maxWidth: '100% !important',
                            minWidth: '0 !important',
                        },
                        '& .filter-field .MuiInputBase-input, & .RaFilterForm-input .MuiInputBase-input': {
                            minWidth: '0 !important',
                        },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important', px: '0 !important', width: '100% !important', maxWidth: '100% !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .column-type': { width: '80px' },
                            '& .column-title': { minWidth: '300px', flex: 1 },
                            '& .column-priority': { width: '110px' },
                            '& .column-status': { width: '110px' },
                            '& .column-stats': { width: '100px' },
                            '& .column-created_at': { width: '150px' },
                            '& .column-actions': { width: '120px' },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.messages.fields.type')}
                            render={(record: any) => (
                                <Tooltip title={translate(`resources.messages.types.${record.type}`)}>
                                    <Box display="flex" alignItems="center" justifyContent="center">
                                        <TypeIcon type={record.type} />
                                    </Box>
                                </Tooltip>
                            )}
                            source="type"
                        />
                        <TextField
                            source="title"
                            label={translate('resources.messages.fields.title')}
                            sx={{
                                '& .MuiTypography-root': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }
                            }}
                        />
                        <FunctionField
                            label={translate('resources.messages.fields.priority')}
                            render={(record: any) => <PriorityChip priority={record.priority} />}
                            source="priority"
                        />
                        <FunctionField
                            label={translate('resources.messages.fields.status')}
                            render={(record: any) => {
                                let status = 'sent';
                                const now = new Date();
                                const validFrom = new Date(record.valid_from);

                                if (validFrom > now) {
                                    status = 'scheduled';
                                } else if (record.total_recipients === 0) {
                                    status = 'draft';
                                }

                                return <StatusChip status={status} />;
                            }}
                            source="status"
                        />
                        <FunctionField
                            label={translate('resources.messages.statistics')}
                            render={(record: any) => (
                                <Box>
                                    <Typography variant="body2" component="div">
                                        {record.total_reads}/{record.total_recipients} {translate('resources.messages.fields.reads')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {record.total_recipients > 0
                                            ? `${Math.round((record.total_reads / record.total_recipients) * 100)}% ${translate('resources.messages.fields.readRate')}`
                                            : translate('resources.messages.targetTypes.all')
                                        }
                                    </Typography>
                                </Box>
                            )}
                            source="stats"
                        />
                        <DateField
                            source="created_at"
                            label={translate('resources.messages.fields.createdAt')}
                            locales="en-GB"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }}
                        />
                        <FunctionField
                            label=""
                            render={(record: any) => (
                                <Box display="flex" gap={0}>
                                    <Tooltip title={translate('ra.action.show')}>
                                        <span>
                                            <ShowButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.edit')}>
                                        <span>
                                            <EditButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={translate('ra.action.delete')}>
                                        <span>
                                            <DeleteButton record={record} size="small" label="" />
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/messages/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default MessageList;
